import { useCallback } from "react"
import { useContextSelector as useCS } from "use-context-selector"
import { AlertTypeEnum } from "@unlimint/admin-ui-kit"

import { errorService } from "infrastructure/services/ErrorService"
import { apiService } from "infrastructure/services/ApiService"
import { ApiMethodEnum } from "infrastructure/enums/ApiMethodEnum"
import { useTranslate } from "infrastructure/hooks/useTranslate"

import { TFetchListParamReq } from "domain/core/types"
import { ReportsContext } from "domain/reports/ReportsContext"
import { reportsSelectors } from "domain/reports/selectors"
import { formatDate } from "domain/core/utils"
import { useAlert } from "domain/application/hooks/useAlert"

import { ReportStatusEnum, ReportTypeEnum } from "../../enums"
import { downloaders } from "./utils"

type TUseCreateReportParams = {
  key: string
  url: string
  fileLabel: string
  params?: TFetchListParamReq
  type?: ReportTypeEnum
}

type TUseCreateReport = {
  status: ReportStatusEnum
  send(): void
}

export const useCreateReport = ({
  key,
  url,
  params,
  fileLabel,
  type = ReportTypeEnum.XLSX,
}: TUseCreateReportParams): TUseCreateReport => {
  const reports = useCS(ReportsContext, reportsSelectors.reports)
  const update = useCS(ReportsContext, reportsSelectors.update)

  const sendAlert = useAlert()
  const errorMsg = useTranslate("reports.error")

  const send = useCallback(async () => {
    try {
      update({
        reports: {
          [key]: ReportStatusEnum.DOWNLOADING,
        },
      })

      const res = await apiService({
        url,
        method: ApiMethodEnum.Post,
        responseType: "stream",
        adapter: "fetch",
        data: params,
      })

      const reader = res.data.getReader()

      const from = params?.filters?.startedAt?.from
      const to = params?.filters?.startedAt?.to
      const fileName =
        from && to
          ? `${fileLabel} ${formatDate(from)}_${formatDate(to)}`
          : fileLabel

      if (type === ReportTypeEnum.CSV) {
        update({
          reports: {
            [key]: ReportStatusEnum.DONE,
          },
        })
        downloaders.CSV(fileName, reader)
      } else {
        update({
          reports: {
            [key]: ReportStatusEnum.CREATION,
          },
        })
        await downloaders.XLSX(fileName, reader)
        update({
          reports: {
            [key]: ReportStatusEnum.DONE,
          },
        })
      }
    } catch (err) {
      errorService.send(err)
      sendAlert(errorMsg, AlertTypeEnum.Error)
      update({
        reports: {
          [key]: null,
        },
      })
    }
  }, [params, type])

  return { status: reports?.[key], send }
}
